import getUrlOverrides from '../utils/overrides/getUrlOverrides';
import calculateLogicalRing from '../utils/calculateLogicalRing';
import type { DefaultFlagMap } from '../utils/defaultFlags';
import { setDefaultFlags } from '../utils/defaultFlags';
import store from '../store/Store';
import getFeatureFlagsIndexerForMailboxInfo from '../utils/getFeatureFlagsIndexerForMailboxInfo';
import { checkIfAnonymousFlightingShouldBeDisabled } from '../utils/anonymousFlighting';
import { getGlobalSettingsAccountIndexer } from 'owa-account-source-list-store';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import type { MailboxInfo } from 'owa-client-types';
import { featureFlagsInitialized } from './featureFlagsInitialized';
import { getEnabledFlights } from '../utils/getEnabledFlights';
import { extractFlagsFromServiceData } from '../utils/extractFlagsFromServiceData';
import { addOverrides } from '../utils/overrides/addOverrides';
import configureQueryStringCaching from '../utils/configureQueryStringCaching';

export type LogicalRing = 'Unknown' | 'Dogfood' | 'Microsoft' | 'FirstRelease' | 'WW';

export const FEATURES_QUERYSTRING_PARAMETER_NAME = 'features';
export const FLIGHT_DEVTOOLS_NAME = 'fwk-devtools';
export const FLIGHT_ALLOW_OVERRIDES_NAME = 'fwk-allow-feature-overrides';

const mailboxesWithInitializedFlags: Set<string> = new Set();
let isNoMailboxFlagsInitialized: boolean = false;
export function areFeatureFlagsInitialized(mailboxInfo?: MailboxInfo): boolean {
    // if we are looking for the default case and it was initialized before, then
    // we can return true as a performance optimization
    if (isNoMailboxFlagsInitialized && !mailboxInfo) {
        return true;
    }

    const result =
        mailboxesWithInitializedFlags.size > 0 &&
        mailboxesWithInitializedFlags.has(getFeatureFlagsIndexerForMailboxInfo(mailboxInfo, true));

    // In the default case, if the value is true, then set isNoMailboxFlagsInitialized
    // so future calls are fast
    if (!mailboxInfo && result) {
        isNoMailboxFlagsInitialized = true;
    }

    return result;
}

// Determines if the feature flags are initialized for a specific indexer
export function areFeatureFlagsInitializedForIndexer(indexer: string): boolean {
    return mailboxesWithInitializedFlags.has(indexer);
}

type FlagsFromServiceObject = {
    [key: string]: boolean;
};

function initializeFeatureFlags(
    flagsFromService: string[],
    localStorageOverrides: Record<string, boolean>,
    mailboxInfo?: MailboxInfo
): LogicalRing {
    const flagsFromServiceObject: FlagsFromServiceObject =
        extractFlagsFromServiceData(flagsFromService);

    const logicalRing = calculateLogicalRing(feature => flagsFromServiceObject[feature]);

    // We don't want to initialize a second time and potentially change flags mid-session
    if (areFeatureFlagsInitialized(mailboxInfo)) {
        return logicalRing;
    }

    const isGlobalSettingsAccount =
        !mailboxInfo ||
        getGlobalSettingsAccountIndexer(/*throwIfNotInitialized*/ false) ===
            getIndexerValueForMailboxInfo(mailboxInfo);

    setDefaultFlags(makeDefaultFlags(flagsFromServiceObject), mailboxInfo || null);

    // honor local storage overrides for non-global accounts if they're stored on a per-account basis
    if (
        (logicalRing == 'Dogfood' || flagsFromServiceObject[FLIGHT_ALLOW_OVERRIDES_NAME]) &&
        localStorageOverrides
    ) {
        addOverrides(flagsFromServiceObject, localStorageOverrides);
    }

    // url overrides only apply to the global account
    if (flagsFromServiceObject[FLIGHT_DEVTOOLS_NAME] && isGlobalSettingsAccount) {
        addOverrides(flagsFromServiceObject, getUrlOverrides(FEATURES_QUERYSTRING_PARAMETER_NAME));
    }

    store(mailboxInfo).featureFlags = new Map<string, boolean>(
        Object.keys(flagsFromServiceObject).map(key => [key, flagsFromServiceObject[key]])
    );

    const indexer = getFeatureFlagsIndexerForMailboxInfo(mailboxInfo, true);
    mailboxesWithInitializedFlags.add(indexer);

    // send satchel action that these were successfully initialized.
    featureFlagsInitialized(getEnabledFlights(mailboxInfo), mailboxInfo, indexer);

    checkIfAnonymousFlightingShouldBeDisabled();

    // Configure querystring caching behavior based on feature flag (only needs to be done once for global account).
    // Do this after flags are initialized.  (Inverting dependency to avoid circular dependency with "isFeatureEnabled" in owa-querystring)
    if (isGlobalSettingsAccount) {
        configureQueryStringCaching();
    }

    return logicalRing;
}

function makeDefaultFlags(flags: FlagsFromServiceObject) {
    const defaultFlags: DefaultFlagMap = {};
    for (const key of Object.keys(flags)) {
        defaultFlags[key] = flags[key];
    }
    return defaultFlags;
}

export default initializeFeatureFlags;

// Allow the unit tests to reset the feature flags
export function resetFeatureFlagsInitializedForUnitTests(): void {
    mailboxesWithInitializedFlags.clear();
}
